import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isAffiliate: boolean;
  isWholesale: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAffiliate, setIsAffiliate] = useState(false);
  const [isWholesale, setIsWholesale] = useState(false);

  // Resolve roles for a given user. Returns once all role lookups settle so
  // callers can safely flip `loading` to false without racing the guards.
  const resolveRoles = async (currentUser: User) => {
    try {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', currentUser.id);

      const nextIsAdmin = roles?.some((r) => r.role === 'admin') ?? false;

      const { data: affiliate } = await supabase
        .from('affiliates')
        .select('id, status')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      const nextIsAffiliate = affiliate?.status === 'approved';

      // Wholesale: first by user_id, else attempt auto-link by email
      let wholesalePartner: { id: string; status: string; user_id: string | null } | null = null;

      const { data: linkedPartner } = await supabase
        .from('wholesale_partners')
        .select('id, status, user_id')
        .eq('user_id', currentUser.id)
        .maybeSingle();

      if (linkedPartner) {
        wholesalePartner = linkedPartner;
      } else if (currentUser.email) {
        const { data: unlinkedPartners } = await supabase
          .from('wholesale_partners')
          .select('id, status, user_id')
          .eq('email', currentUser.email.toLowerCase())
          .is('user_id', null);

        if (unlinkedPartners && unlinkedPartners.length === 1) {
          const { error: linkError } = await supabase
            .from('wholesale_partners')
            .update({ user_id: currentUser.id })
            .eq('id', unlinkedPartners[0].id);

          if (!linkError) {
            wholesalePartner = { ...unlinkedPartners[0], user_id: currentUser.id };
          }
        }
      }

      const nextIsWholesale = wholesalePartner?.status === 'approved';

      setIsAdmin(nextIsAdmin);
      setIsAffiliate(!!nextIsAffiliate);
      setIsWholesale(!!nextIsWholesale);
    } catch (err) {
      console.error('[AuthContext] role resolution failed', err);
      setIsAdmin(false);
      setIsAffiliate(false);
      setIsWholesale(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        if (!mounted) return;
        setSession(nextSession);
        setUser(nextSession?.user ?? null);

        if (nextSession?.user) {
          // Defer Supabase calls out of the auth callback to avoid deadlocks
          setTimeout(() => {
            if (!mounted) return;
            resolveRoles(nextSession.user).finally(() => {
              if (mounted) setLoading(false);
            });
          }, 0);
        } else {
          setIsAdmin(false);
          setIsAffiliate(false);
          setIsWholesale(false);
          setLoading(false);
        }
      }
    );

    // THEN check for an existing session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      setSession(initialSession);
      setUser(initialSession?.user ?? null);

      if (initialSession?.user) {
        resolveRoles(initialSession.user).finally(() => {
          if (mounted) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: fullName },
        emailRedirectTo: `${window.location.origin}/auth/login`
      }
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signIn,
      signUp,
      signOut,
      isAdmin,
      isAffiliate,
      isWholesale
    }}>
      {children}
    </AuthContext.Provider>
  );
};
